import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import AnimatedSection from "../components/Courses/Shared/AnimatedSection";
import ProfessionalCourseApplicationForm from "../components/Courses/ProfessionalCourses/ProfessionalCourseApplicationForm";
import comptiaImg from "../assets/comptia.png";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import {
  parseOverviewJson,
  slugifyCert,
  scrollToId,
  CERT_CATEGORIES,
  DISCOVER_TABS,
  inferCertMeta,
  DEFAULT_DISCOVER_COPY,
} from "../utils/comptiaCerts";

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Render plain CMS text: split by blank lines into paragraphs.
function CmsText({ text, className = "" }) {
  const paras = text.split(/\n\n+/).filter(Boolean);
  return (
    <>
      {paras.map((p, i) => (
        <p key={i} className={`text-base md:text-lg text-gray-800 leading-relaxed mb-4 whitespace-pre-wrap ${className}`}>
          {p}
        </p>
      ))}
    </>
  );
}

// Grey placeholder shown when no image is uploaded.
function ImagePlaceholder({ label }) {
  return (
    <div className="mt-8 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center py-14 px-6 text-gray-400">
      <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <p className="text-sm text-center max-w-xs">{label}</p>
    </div>
  );
}

// Image or placeholder.
function SectionImage({ url, alt, label }) {
  if (url) {
    return (
      <div className="mt-8">
        <img
          src={getImageUrl(url)}
          alt={alt}
          className="w-full rounded-2xl object-cover shadow-md"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
    );
  }
  return <ImagePlaceholder label={label} />;
}

// ─── JSON parsers ─────────────────────────────────────────────────────────────

// Parse comptiaBenefitsSection: expects { intro, cards: [{title, body}] }
function parseBenefitsJson(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.cards)) return parsed;
  } catch {}
  return null;
}

// ─── Quick Navigation sidebar ────────────────────────────────────────────────

const TOC_ITEMS = [
  { id: "who-is-comptia",         label: "Who is CompTIA?" },
  { id: "certification-overview", label: "Discover Our Certifications" },
  { id: "why-study",              label: "Why Study CompTIA at JRIIT? (Benefits)" },
  { id: "admission-requirements", label: "Admission Requirements" },
  { id: "application-form",       label: "Apply Now" },
];

function QuickNavigation({ activeSection }) {
  const scrollTo = useCallback((id) => scrollToId(id), []);

  return (
    <nav className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
      <div className="bg-custom-pink px-5 py-4">
        <p className="text-white font-bold text-sm uppercase tracking-widest">Quick Navigation</p>
      </div>
      <ol className="p-4 space-y-1">
        {TOC_ITEMS.map((item, idx) => {
          const isActive = activeSection === item.id;
          return (
            <li key={item.id}>
              <button
                onClick={() => scrollTo(item.id)}
                className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-custom-pink/10 text-custom-pink font-semibold border-l-4 border-custom-pink pl-2"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold mt-0.5 ${
                    isActive ? "bg-custom-pink text-white" : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {idx + 1}
                </span>
                <span className="leading-snug">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ─── Section components ───────────────────────────────────────────────────────

function WhoIsCompTIA({ text, imageUrl }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative py-16 md:py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      <div className="absolute top-10 right-8 w-64 h-64 bg-custom-pink/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-8 w-72 h-72 bg-gold/8 rounded-full blur-3xl pointer-events-none" />
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className={`text-center mb-10 transform transition-all duration-700 ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-custom-pink leading-tight">Who is CompTIA?</h2>
          <div className="w-28 h-1 bg-gold mx-auto rounded-full mt-4" />
        </div>
        <div className={`bg-white/70 backdrop-blur-sm p-8 md:p-12 rounded-2xl transform transition-all duration-700 delay-200 ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {text && <CmsText text={text} />}
          <SectionImage url={imageUrl} alt="Who is CompTIA" label="CompTIA Overview Image — upload via dashboard" />
        </div>
      </div>
    </section>
  );
}

// ─── "Discover Our Certifications" catalog widget ────────────────────────────

function DiscoverCertifications({
  certs,
  onSelectCert,
  heading,
  subtitle,
  cardTitle,
  cardBody,
  ctaLabel,
  skillAreaHeading,
}) {
  const [tab, setTab] = useState("overview");
  const enriched = certs.map((c) => ({ ...c, ...inferCertMeta(c) }));
  const visibleCerts = tab === "all" ? enriched : enriched.filter((c) => c.category === tab);

  const copy = {
    heading: heading || DEFAULT_DISCOVER_COPY.heading,
    subtitle: subtitle || DEFAULT_DISCOVER_COPY.subtitle,
    cardTitle: cardTitle || DEFAULT_DISCOVER_COPY.cardTitle,
    cardBody: cardBody || DEFAULT_DISCOVER_COPY.cardBody,
    ctaLabel: ctaLabel || DEFAULT_DISCOVER_COPY.ctaLabel,
    skillAreaHeading: skillAreaHeading || DEFAULT_DISCOVER_COPY.skillAreaHeading,
  };

  return (
    <div className="mb-14">
      <div className="text-center mb-8">
        <h3 className="text-3xl md:text-4xl font-extrabold text-custom-pink">{copy.heading}</h3>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto whitespace-pre-wrap">{copy.subtitle}</p>
      </div>

      {/* Category tab bar */}
      <div className="flex flex-wrap justify-center bg-gray-900 rounded-xl overflow-hidden mb-8">
        {DISCOVER_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-5 md:px-6 py-3 text-sm md:text-base font-semibold tracking-wide transition-colors border-b-4 ${
              tab === t.key ? "text-white border-custom-pink" : "text-gray-400 border-transparent hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-8 md:p-10">
            <h4 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 leading-snug">{copy.cardTitle}</h4>
            {copy.cardBody.split(/\n\n+/).filter(Boolean).map((p, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mb-3 last:mb-6 whitespace-pre-wrap">{p}</p>
            ))}
            <button
              type="button"
              onClick={() => setTab("all")}
              className="inline-block bg-custom-pink text-white font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              {copy.ctaLabel}
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
            <h5 className="text-lg font-bold text-gray-900 mb-4">{copy.skillAreaHeading}</h5>
            <ul className="space-y-2">
              {CERT_CATEGORIES.map((c) => (
                <li key={c.key} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-custom-pink flex-shrink-0" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => setTab(c.key)}
                    className="text-custom-pink font-semibold hover:underline"
                  >
                    {c.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {visibleCerts.length > 0 ? (
            visibleCerts.map((cert, i) => (
              <div
                key={i}
                onClick={() => onSelectCert(cert)}
                onKeyDown={(e) => { if (e.key === "Enter") onSelectCert(cert); }}
                role="button"
                tabIndex={0}
                className="text-center bg-white border border-gray-200 rounded-2xl p-6 flex flex-col items-center cursor-pointer hover:shadow-md hover:border-custom-pink/30 transition-all"
              >
                {cert.badge_image ? (
                  <img
                    src={getImageUrl(cert.badge_image)}
                    alt={cert.name}
                    className="w-16 h-16 object-contain mb-4"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 mb-4" />
                )}
                <p className="font-bold text-gray-900 text-lg mb-2">{cert.name}</p>
                {cert.tag && (
                  <div className="flex flex-col items-center gap-1.5 mb-4">
                    {cert.tag.split("\n").map((line) => line.trim()).filter(Boolean).map((line, idx) => (
                      <span key={idx} className="inline-block text-xs font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                        {line}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onSelectCert(cert); }}
                  className="mt-auto text-custom-pink font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all"
                >
                  Learn More <span aria-hidden="true">&rarr;</span>
                </button>
              </div>
            ))
          ) : (
            <p className="col-span-full text-center text-gray-500 italic">No certifications in this category yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

function CertificationOverview({ rawJson }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const parsed = parseOverviewJson(rawJson);

  // Navigate to the certification's own route (e.g. /courses/professional-courses/comptia/comptia-a),
  // mirroring how CompTIA's own site links from its catalog into each certification's page.
  const handleSelectCert = (cert) => {
    navigate(`/courses/professional-courses/comptia/${slugifyCert(cert.name)}`);
  };

  return (
    <section ref={ref} className="relative py-16 md:py-24 bg-white overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {parsed && (
          <div className={`transform transition-all duration-700 ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <DiscoverCertifications
              certs={parsed.certs}
              onSelectCert={handleSelectCert}
              heading={parsed.discoverHeading}
              subtitle={parsed.discoverSubtitle}
              cardTitle={parsed.discoverCardTitle}
              cardBody={parsed.discoverCardBody}
              ctaLabel={parsed.discoverCtaLabel}
              skillAreaHeading={parsed.discoverSkillAreaHeading}
            />
          </div>
        )}

        {!parsed && rawJson && (
          <div className={`transform transition-all duration-700 delay-200 ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <CmsText text={rawJson} />
          </div>
        )}
      </div>
    </section>
  );
}

function BenefitsOfCompTIA({ rawJson, imageUrl }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const parsed = parseBenefitsJson(rawJson);
  const intro = parsed?.intro ?? "";
  const cards = parsed?.cards ?? [];

  return (
    <section ref={ref} className="relative py-16 md:py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      <div className="absolute top-10 right-8 w-64 h-64 bg-gold/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-8 w-72 h-72 bg-custom-pink/8 rounded-full blur-3xl pointer-events-none" />
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className={`text-center mb-4 transform transition-all duration-700 ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-custom-pink leading-tight">Why Study CompTIA at JRIIT? <span className="text-2xl md:text-3xl font-semibold text-gray-500">(Benefits)</span></h2>
          <div className="w-28 h-1 bg-gold mx-auto rounded-full mt-4" />
        </div>
        {intro && (
          <div className={`text-center mb-10 transform transition-all duration-700 delay-100 ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">{intro}</p>
          </div>
        )}

        {cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {cards.map((card, idx) => (
              <div
                key={idx}
                className={`flex gap-4 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-custom-pink/30 transition-all duration-300 transform ${
                  visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                }`}
                style={{ transitionDelay: `${200 + idx * 70}ms` }}
              >
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-custom-pink text-white flex items-center justify-center font-bold text-sm">
                  {idx + 1}
                </div>
                <div>
                  <p className="font-bold text-gray-900 mb-1">{card.title}</p>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">{card.body}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className={`transform transition-all duration-700 delay-700 ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <SectionImage url={imageUrl} alt="Benefits of CompTIA" label="Benefits of CompTIA Image — upload via dashboard" />
        </div>
      </div>
    </section>
  );
}

// ─── Active-section tracker ───────────────────────────────────────────────────

const SECTION_IDS = TOC_ITEMS.map((t) => t.id);

function useActiveSection() {
  const [active, setActive] = useState(SECTION_IDS[0]);

  useEffect(() => {
    const update = () => {
      const navbar = document.querySelector("header.sticky");
      const offset = (navbar ? navbar.getBoundingClientRect().height : 100) + 24;

      // Walk sections top-to-bottom; the last one whose top edge is at or above the
      // offset threshold is the one currently in view.
      let current = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= offset) {
          current = id;
        }
      }
      setActive(current);
    };

    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, []);

  return active;
}

// ─── Main component ───────────────────────────────────────────────────────────

const CompTIADetailPage = () => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeSection = useActiveSection();

  useEffect(() => {
    cmsApi
      .getProgramBySlug("comptia-certification")
      .then((res) => {
        if (res.success && res.program) setProgram(res.program);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // ── data derivation ──
  const admissionText = program?.admissionRequirements ?? program?.requirements ?? "";

  // ── CMS fields ──
  const comptiaWhoSection      = program?.comptiaWhoSection      ?? null;
  const comptiaWhoImage        = program?.comptiaWhoImage        ?? null;
  const comptiaOverviewSection = program?.comptiaOverviewSection ?? null;
  const comptiaBenefitsSection = program?.comptiaBenefitsSection ?? null;
  const comptiaBenefitsImage   = program?.comptiaBenefitsImage   ?? null;

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
        {/* ── Hero (full-width, above TOC) ── */}
        <CourseHeader
          title="CompTIA"
          highlight="Certification Programs"
          subtitle="Vendor-neutral IT certifications — from an authorised training partner in Tanzania."
          backgroundImage={program?.heroImage ? getImageUrl(program.heroImage) : comptiaImg}
          cta={{ label: "Start Application", targetId: "application-form" }}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Professional Courses", href: "/course/professional-courses" },
            { label: "CompTIA" },
          ]}
        />

        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink" />
          </div>
        ) : (
          /* ── Two-column layout: TOC left, content right ── */
          <div className="max-w-[1500px] mx-auto px-6">
          <div className="flex items-start gap-8">

            {/* Left: sticky Quick Navigation (hidden on mobile) */}
            <aside className="hidden lg:block w-72 shrink-0 sticky top-20 self-start h-fit pt-10 pl-4 xl:pl-6">
              <QuickNavigation activeSection={activeSection} />
            </aside>

            {/* Right: all page content */}
            <div className="flex-1 min-w-0">

              {/* 1. Who is CompTIA? */}
              <div id="who-is-comptia">
                <WhoIsCompTIA text={comptiaWhoSection} imageUrl={comptiaWhoImage} />
              </div>

              {/* 2. Certification Overview — cert list from CMS JSON */}
              <div id="certification-overview">
                <CertificationOverview rawJson={comptiaOverviewSection} />
              </div>

              {/* 3. Why Study CompTIA at JRIIT? (Benefits) */}
              <div id="why-study">
                <BenefitsOfCompTIA rawJson={comptiaBenefitsSection} imageUrl={comptiaBenefitsImage} />
              </div>

              {/* 4. Admission Requirements */}
              <div id="admission-requirements">
                <AnimatedSection title="ADMISSION REQUIREMENTS">
                  <p className="text-base md:text-lg lg:text-xl text-gray-800 leading-loose tracking-wide">
                    {admissionText}
                  </p>
                </AnimatedSection>
              </div>

              {/* 5. Application Form */}
              <div id="application-form">
                <ProfessionalCourseApplicationForm programName="CompTIA" fieldLabel="Professional Course" />
              </div>

            </div>
          </div>
          </div>
        )}
      </div>
      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default CompTIADetailPage;
